/**
 * Firebase Configuration - Options Oracle V2
 * Centralized Firebase initialization with Auth & Firestore
 */
import { type FirebaseApp, initializeApp } from 'firebase/app'
import { type Auth, browserLocalPersistence, GoogleAuthProvider, getAuth, setPersistence } from 'firebase/auth'
import { type Firestore, initializeFirestore } from 'firebase/firestore'

// Firebase configuration from environment or hardcoded fallback
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'AIzaSyCLHd4v1qVKDpc1cuea0hmS0nHQtd3a238',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'studio-4081197442-105d3.firebaseapp.com',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'studio-4081197442-105d3',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'studio-4081197442-105d3.firebasestorage.app',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '1039446706859',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:1039446706859:web:0b4c543827e869958c05ee',
}

// Singleton instances
let app: FirebaseApp | null = null
let auth: Auth | null = null
let db: Firestore | null = null
let googleProvider: GoogleAuthProvider | null = null

/**
 * Initialize Firebase App (Singleton Pattern)
 * Safe to call multiple times - returns existing instance
 */
export function getFirebaseApp(): FirebaseApp {
    if (!app) {
        app = initializeApp(firebaseConfig)
        // console.log("[Firebase] App initialized");
    }
    return app
}

/**
 * Get Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
    if (!auth) {
        const firebaseApp = getFirebaseApp()
        auth = getAuth(firebaseApp)

        // Set persistence to local (survives browser restart)
        if (typeof window !== 'undefined') {
            setPersistence(auth, browserLocalPersistence).catch((error) => {
                console.error('[Firebase] Persistence Error:', error)
            })
        }

        // console.log("[Firebase] Auth initialized");
    }
    return auth
}

/**
 * Get Firestore instance with optimized settings
 */
export function getFirestore(): Firestore {
    if (!db) {
        const firebaseApp = getFirebaseApp()
        db = initializeFirestore(firebaseApp, {
            experimentalForceLongPolling: true, // Avoid QUIC errors
        })
        // console.log("[Firebase] Firestore initialized");
    }
    return db
}

/**
 * Get Google Auth Provider
 */
export function getGoogleProvider(): GoogleAuthProvider {
    if (!googleProvider) {
        googleProvider = new GoogleAuthProvider()
        googleProvider.setCustomParameters({
            prompt: 'select_account',
        })
    }
    return googleProvider
}

// Export convenience getters
export { auth, db, googleProvider }
